import { useCallback, useEffect, useMemo, useState } from 'react';

import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { enUS, ptBR } from 'date-fns/locale';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import {
  FiChevronLeft,
  FiRadio,
  FiCalendar,
  FiUsers,
  FiX,
  FiMonitor,
  FiPlay,
  FiClock,
} from 'react-icons/fi';
import ReactSelect from 'react-select';
import { useTheme } from 'styled-components';

import { MetaTags, Pagination, SpeakersBubbles } from '@components';
import {
  useEventfy,
  usePagination,
  useScheduleQuery,
  useSelectRoomsQuery,
  useSelectScheduleDatesQuery,
  useSpeakersQuery,
} from '@hooks';
import { selectHasMoreThanOneRoom } from '@hooks/useEventfy/selectors';
import { generateArray } from '@utils';
import { HistoryState } from '@services/history';
import {
  SelectRoomsData,
  FullScheduleData,
  FullSpeakerData,
} from '@common/types/api';

import {
  LiveHeroWrapper,
  LiveBadge as RoomLiveBadge,
  LiveBadgeDot,
  LiveGlassCard,
  LiveRoomName,
  LiveNowPlaying,
  LiveNowPlayingHeader,
  LiveNowLabel,
  EqualizerBars,
  EqualizerBar,
  LiveSessionTitle as RoomLiveSessionTitle,
  LiveSpeakerRow,
  LiveSpeakerAvatar,
  LiveSpeakerFallback,
  LiveSpeakerNames,
  LiveProgressWrapper,
  LiveProgressTrack,
  LiveProgressFill,
  LiveProgressTime,
  LiveEnterButton,
  IdleHeroWrapper,
  IdleBadge,
  IdleDot,
  NextUpCard as RoomNextUpCard,
  NextUpLabel,
  NextUpRoomName,
  NextUpSessionTitle as RoomNextUpSessionTitle,
  NextUpDateTime,
  NextUpCountdown,
} from '../Rooms/styles';
import * as S from './styles';

type HandlePagination = (selectedItem: { selected: number }) => void;

const SKELETON_COUNT = 4;
const SIDEBAR_MAX_SPEAKERS = 3;
const I18N_SCHEDULE_TITLE = 'pages.schedule.title';
const I18N_SPEAKERS_TITLE = 'pages.speakers.title';

// ── Helpers ─────────────────────────────────────────────

const isSessionLive = (session: FullScheduleData) => {
  if (!session.schedule_date || !session.start_time || !session.end_time) {
    return false;
  }
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  return isPast(start) && isFuture(end);
};

const findNextSession = (
  schedule: FullScheduleData[],
  live: FullScheduleData | undefined,
) => {
  const now = new Date();
  const upcoming = schedule
    .filter((s) => {
      const start = new Date(`${s.schedule_date}T${s.start_time}`);
      return start > now && s !== live;
    })
    .sort((a, b) => {
      const aStart = new Date(`${a.schedule_date}T${a.start_time}`);
      const bStart = new Date(`${b.schedule_date}T${b.start_time}`);
      return aStart.getTime() - bStart.getTime();
    });
  return { session: upcoming[0] };
};

const formatDates = (data: { value: string; label: string }[] | undefined) =>
  data?.map(({ value }) => {
    const parsedDate = parseISO(value);
    const formattedDate = format(parsedDate, 'dd MMM', { locale: enUS });
    const [day, month] = formattedDate.split(' ');
    return { day, month, value };
  });

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

// ── Filter Sub-components ───────────────────────────────

const RoomFilters = ({
  rooms,
  activeRoomId,
  onFilter,
}: {
  rooms: SelectRoomsData[];
  activeRoomId: string | null;
  onFilter: (room?: SelectRoomsData) => void;
}) => {
  const options = [
    { value: '', label: 'Todas' },
    ...rooms.map((r) => ({ value: String(r.value), label: r.label })),
  ];

  const activeOption =
    options.find((o) => o.value === (activeRoomId || '')) || options[0];

  const handleChange = (opt: { value: string; label: string } | null) => {
    if (!opt || !opt.value) {
      onFilter();
      return;
    }
    const selected = rooms.find((r) => String(r.value) === opt.value);
    onFilter(selected);
  };

  return (
    <S.RoomDropdown>
      <ReactSelect
        value={activeOption}
        onChange={handleChange}
        options={options}
        isSearchable={false}
        menuPlacement="auto"
        className="room-select-container"
        classNamePrefix="room-select"
        components={{
          SingleValue: ({
            data,
          }: {
            data: { value: string; label: string };
          }) => (
            <div className="room-select__custom-value">
              <FiMonitor size={14} className="room-value-icon" />
              <span className="room-value-label">Sala:</span>
              <strong className="room-value-name">
                {data.value === '' ? 'Todas' : data.label}
              </strong>
            </div>
          ),
        }}
        formatOptionLabel={(opt) => (
          <span>{opt.value === '' ? 'Todas as salas' : opt.label}</span>
        )}
      />
    </S.RoomDropdown>
  );
};

type FormattedDate = { day: string; month: string; value: string };

const DayTabList = ({
  dates,
  activeDate,
  onSelect,
}: {
  dates: FormattedDate[] | undefined;
  activeDate: string | null;
  onSelect: (date: string) => () => void;
}) => {
  const { t } = useTranslation();

  return (
    <S.DayTabs>
      {dates?.map((day) => {
        const isActive = activeDate === day.value;
        return (
          <S.DayTabWrapper key={day.value}>
            <S.DayTab
              type="button"
              $active={isActive}
              onClick={onSelect(day.value)}
            >
              <strong>{day.day}</strong>
              <span>{t(`date.month.${day.month}`)}</span>
            </S.DayTab>
            {isActive && (
              <S.DayTabClose type="button" onClick={onSelect(day.value)}>
                <FiX size={10} />
              </S.DayTabClose>
            )}
          </S.DayTabWrapper>
        );
      })}
    </S.DayTabs>
  );
};

// ── Session Grid Sub-components ─────────────────────────

const SessionList = ({
  sessions,
  onSessionClick,
}: {
  sessions: FullScheduleData[];
  onSessionClick: (id: number) => void;
}) => {
  const { t } = useTranslation();

  if (sessions.length === 0) {
    return (
      <S.EmptyState>
        <FiCalendar size={44} />
        <strong>
          {t('pages.schedule.empty', 'Nenhuma sessão encontrada')}
        </strong>
        <span>
          {t('pages.schedule.emptyHint', 'Tente ajustar os filtros.')}
        </span>
      </S.EmptyState>
    );
  }

  return (
    <>
      {sessions.map((session) => (
        <SessionCard
          key={`session-${session.id}`}
          session={session}
          onClick={() => onSessionClick(session.id)}
        />
      ))}
    </>
  );
};

const SkeletonCards = () => (
  <>
    {generateArray(SKELETON_COUNT).map((key) => (
      <S.SkeletonCard key={key}>
        <div>
          <Skeleton width={58} height={22} />
          <Skeleton
            width={40}
            height={10}
            style={{ marginTop: SKELETON_COUNT }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Skeleton width="80%" height={16} />
          <Skeleton count={2} height={10} style={{ marginTop: 8 }} />
          <Skeleton width="50%" height={10} style={{ marginTop: 12 }} />
        </div>
      </S.SkeletonCard>
    ))}
  </>
);

const SessionCard = ({
  session,
  onClick,
}: {
  session: FullScheduleData;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const live = isSessionLive(session);
  const startTime = session.start_time?.slice(0, 5) ?? '';
  const endTime = session.end_time?.slice(0, 5) ?? '';
  const speakers = session.participants?.data ?? [];
  const roomName = session.room?.data?.name;

  return (
    <S.SessionCard
      $color={theme.colors.brand.primary.default}
      onClick={onClick}
    >
      {roomName && (
        <S.RoomBadgeCard>
          <FiRadio size={10} />
          {roomName}
        </S.RoomBadgeCard>
      )}

      <S.CardInner>
        <S.TimeBlock>
          <strong>{startTime}</strong>
          <span>{endTime}</span>
          {live && (
            <S.LiveChip>
              <span />
              LIVE
            </S.LiveChip>
          )}
        </S.TimeBlock>

        <S.CardContent>
          <S.CardTitle>{session.title}</S.CardTitle>
          <S.CardDesc>{session.description}</S.CardDesc>

          {speakers.length > 0 && (
            <S.CardFooter>
              <S.CardSpeakers>
                <span>{t(I18N_SPEAKERS_TITLE)}</span>
                <SpeakersBubbles
                  data={speakers}
                  innerKey={`session-${session.id}-room-${session.room_id}`}
                />
              </S.CardSpeakers>
            </S.CardFooter>
          )}
        </S.CardContent>
      </S.CardInner>
    </S.SessionCard>
  );
};

// ── Sidebar Sub-components ──────────────────────────────

// ── Progress & Equalizer helpers ────────────────────────

const getSessionProgress = (session: FullScheduleData): number => {
  if (!session.schedule_date || !session.start_time || !session.end_time)
    return 0;
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  const now = new Date();
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  return ((now.getTime() - start.getTime()) / total) * 100;
};

const EQ_DELAYS = [0, 0.2, 0.4, 0.15];
const fmtTime = (t: string) => (t ? t.slice(0, 5) : '');

// ── Sidebar Live/NoLive cards (same as /rooms) ─────────

const ScheduleLiveCard = ({ session }: { session: FullScheduleData }) => {
  const roomSlug = session.room?.data?.slug;
  const roomName = session.room?.data?.name;
  const speakers = session.participants?.data?.slice(0, 3) ?? [];
  const names = speakers.map((s) => s.name.split(' ')[0]).join(', ');
  const [progress, setProgress] = useState(getSessionProgress(session));

  useEffect(() => {
    setProgress(getSessionProgress(session));
    const timer = setInterval(() => {
      setProgress(getSessionProgress(session));
    }, 10000);
    return () => clearInterval(timer);
  }, [session]);

  return (
    <LiveHeroWrapper style={{ margin: '0 0 1rem' }}>
      <RoomLiveBadge>
        <LiveBadgeDot />
        AO VIVO
      </RoomLiveBadge>

      <LiveGlassCard>
        {roomName && (
          <LiveRoomName>
            <FiMonitor size={14} />
            {roomName}
          </LiveRoomName>
        )}

        <LiveNowPlaying>
          <LiveNowPlayingHeader>
            <EqualizerBars>
              {EQ_DELAYS.map((d) => (
                <EqualizerBar key={`eq-${d}`} $delay={d} />
              ))}
            </EqualizerBars>
            <LiveNowLabel>ACONTECENDO AGORA</LiveNowLabel>
          </LiveNowPlayingHeader>
          <RoomLiveSessionTitle>{session.title}</RoomLiveSessionTitle>
        </LiveNowPlaying>

        {speakers.length > 0 && (
          <LiveSpeakerRow>
            {speakers.map((sp, i) =>
              sp.photo ? (
                <LiveSpeakerAvatar
                  key={sp.id}
                  src={sp.photo}
                  alt={sp.name}
                  $index={i}
                />
              ) : (
                <LiveSpeakerFallback key={sp.id} $index={i}>
                  {getInitials(sp.name)}
                </LiveSpeakerFallback>
              ),
            )}
            <LiveSpeakerNames>{names}</LiveSpeakerNames>
          </LiveSpeakerRow>
        )}

        <LiveProgressWrapper>
          <LiveProgressTime>{fmtTime(session.start_time)}</LiveProgressTime>
          <LiveProgressTrack>
            <LiveProgressFill $percent={progress} />
          </LiveProgressTrack>
          <LiveProgressTime>{fmtTime(session.end_time)}</LiveProgressTime>
        </LiveProgressWrapper>
      </LiveGlassCard>

      <LiveEnterButton to={roomSlug ? `/broadcast/${roomSlug}` : '#'}>
        <FiPlay size={14} />
        Entrar na sala
      </LiveEnterButton>
    </LiveHeroWrapper>
  );
};

const ScheduleNoLiveCard = ({ session }: { session: FullScheduleData }) => {
  const startDate = session.schedule_date
    ? parseISO(`${session.schedule_date}T${session.start_time || '00:00'}`)
    : new Date();
  const countdown = formatDistanceToNow(startDate, {
    locale: ptBR,
    addSuffix: true,
  });

  return (
    <IdleHeroWrapper style={{ margin: '0 0 1rem' }}>
      <IdleBadge>
        <IdleDot />
        Nenhuma transmissão ao vivo
      </IdleBadge>

      <RoomNextUpCard>
        <NextUpLabel>
          <FiClock size={12} />
          Próxima transmissão
        </NextUpLabel>

        {session.room?.data?.name && (
          <NextUpRoomName>{session.room.data.name}</NextUpRoomName>
        )}

        <RoomNextUpSessionTitle>{session.title}</RoomNextUpSessionTitle>

        <NextUpDateTime>
          <span>
            <FiCalendar size={12} />
            {session.schedule_date
              ? format(parseISO(session.schedule_date), 'dd MMM', {
                  locale: ptBR,
                })
              : ''}
          </span>
          <span>
            <FiClock size={12} />
            {fmtTime(session.start_time)}
          </span>
          <NextUpCountdown>· {countdown}</NextUpCountdown>
        </NextUpDateTime>
      </RoomNextUpCard>
    </IdleHeroWrapper>
  );
};

const SpeakerListItem = ({
  speaker,
  onClick,
}: {
  speaker: FullSpeakerData;
  onClick: () => void;
}) => (
  <S.SpeakerItem onClick={onClick}>
    <S.SpeakerAvatar>
      {speaker.photo ? (
        <img src={speaker.photo} alt={speaker.name} />
      ) : (
        <S.SpeakerAvatarFallback>
          {getInitials(speaker.name)}
        </S.SpeakerAvatarFallback>
      )}
    </S.SpeakerAvatar>
    <S.SpeakerInfo>
      <S.SpeakerName>{speaker.name}</S.SpeakerName>
      {speaker.title && <S.SpeakerTitle>{speaker.title}</S.SpeakerTitle>}
    </S.SpeakerInfo>
  </S.SpeakerItem>
);

const SpeakersSidebar = ({
  isLoading,
  speakers,
  liveSession,
  nextSession,
  onSpeakerClick,
  speakersLabel,
  onViewAll,
}: {
  isLoading: boolean;
  speakers: FullSpeakerData[];
  liveSession: FullScheduleData | undefined;
  nextSession: FullScheduleData | undefined;
  onSpeakerClick: (id: number) => void;
  speakersLabel: string;
  onViewAll: () => void;
}) => {
  const visibleSpeakers = speakers.slice(0, SIDEBAR_MAX_SPEAKERS);

  const renderSpeakers = () => {
    if (isLoading) {
      return (
        <S.SpeakersList>
          {generateArray(SIDEBAR_MAX_SPEAKERS).map((key) => (
            <S.SpeakerItem key={key} as="div">
              <Skeleton circle width="2.75rem" height="2.75rem" />
              <S.SpeakerInfo>
                <Skeleton width="70%" height="0.8rem" />
                <Skeleton width="50%" height="0.65rem" />
              </S.SpeakerInfo>
            </S.SpeakerItem>
          ))}
        </S.SpeakersList>
      );
    }

    if (visibleSpeakers.length === 0) {
      return (
        <S.SidebarEmptyState>
          <FiUsers size={32} />
          <strong>Nenhum palestrante ainda</strong>
          <span>
            Os palestrantes aparecerão aqui assim que forem cadastrados.
          </span>
        </S.SidebarEmptyState>
      );
    }

    return (
      <S.SpeakersList>
        {visibleSpeakers.map((speaker) => (
          <SpeakerListItem
            key={speaker.id}
            speaker={speaker}
            onClick={() => onSpeakerClick(speaker.id)}
          />
        ))}
      </S.SpeakersList>
    );
  };

  return (
    <S.Sidebar>
      {liveSession ? (
        <ScheduleLiveCard session={liveSession} />
      ) : (
        nextSession && <ScheduleNoLiveCard session={nextSession} />
      )}

      <S.SpeakersSection>
        <S.SidebarSectionTitle>
          <FiUsers size={16} />
          {speakersLabel}
        </S.SidebarSectionTitle>

        {renderSpeakers()}

        {speakers.length > SIDEBAR_MAX_SPEAKERS && (
          <>
            <S.SpeakersListFade />
            <S.ViewAllLink type="button" onClick={onViewAll}>
              Ver todos palestrantes →
            </S.ViewAllLink>
          </>
        )}
      </S.SpeakersSection>
    </S.Sidebar>
  );
};

// ── URL Params Hook ─────────────────────────────────────

const useScheduleFilters = () => {
  const history = useHistory<HistoryState>();
  const { pathname, search } = useLocation<HistoryState>();

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const currentPage = Number(queryParams.get('page') || '1');
  const roomId = queryParams.get('roomId');
  const scheduleDate = queryParams.get('date');

  const handleDateFilter = useCallback(
    (newDate: string) => () => {
      if (scheduleDate === newDate) {
        queryParams.delete('date');
      } else {
        queryParams.set('date', newDate);
      }
      history.push({ search: `?${queryParams.toString()}` });
    },
    [queryParams, scheduleDate, history],
  );

  const handleRoomFilter = useCallback(
    (room?: SelectRoomsData) => {
      if (!room || roomId === String(room.value)) {
        queryParams.delete('roomId');
      } else {
        queryParams.set('roomId', String(room.value));
      }
      history.push({ search: `?${queryParams.toString()}` });
    },
    [queryParams, roomId, history],
  );

  const handlePageNavigation = useCallback<HandlePagination>(
    ({ selected }) => {
      queryParams.set('page', String(selected + 1));
      history.push({ search: `?${queryParams.toString()}` });
    },
    [history, queryParams],
  );

  const handleSession = useCallback(
    (sessionId: number) => {
      const params = new URLSearchParams(search);
      params.set('session', String(sessionId));
      history.push({ pathname, search: `?${params.toString()}` });
    },
    [history, pathname, search],
  );

  const goBack = useCallback(() => history.goBack(), [history]);

  return {
    currentPage,
    roomId,
    scheduleDate,
    goBack,
    handleDateFilter,
    handleRoomFilter,
    handlePageNavigation,
    handleSession,
  };
};

// ── Main Component ──────────────────────────────────────

const Schedule = () => {
  const history = useHistory<HistoryState>();
  const { search: locationSearch } = useLocation<HistoryState>();
  const eventHasManyRooms = useEventfy(selectHasMoreThanOneRoom);
  const { t } = useTranslation();

  const {
    currentPage,
    roomId,
    scheduleDate,
    goBack,
    handleDateFilter,
    handleRoomFilter,
    handlePageNavigation,
    handleSession,
  } = useScheduleFilters();

  const selectRoomsQuery = useSelectRoomsQuery(eventHasManyRooms);
  const selectDatesQuery = useSelectScheduleDatesQuery();

  const scheduleQuery = useScheduleQuery(currentPage, {
    date: scheduleDate || undefined,
    roomId: roomId ? +roomId : undefined,
  });

  const allScheduleQuery = useScheduleQuery(1, {});
  const allSchedule = useMemo(
    () => allScheduleQuery.data?.schedule ?? [],
    [allScheduleQuery.data?.schedule],
  );

  const speakersQuery = useSpeakersQuery(1);
  const allSpeakers = useMemo(
    () => speakersQuery.data?.speakers ?? [],
    [speakersQuery.data?.speakers],
  );

  const pagination = usePagination(
    scheduleQuery.data?.meta.pagination.total_pages,
  );

  const formattedDates = useMemo(
    () => formatDates(selectDatesQuery.data),
    [selectDatesQuery.data],
  );

  const liveSession = useMemo(
    () => allSchedule.find(isSessionLive),
    [allSchedule],
  );

  const { session: nextSession } = useMemo(
    () => findNextSession(allSchedule, liveSession),
    [allSchedule, liveSession],
  );

  const handleSpeakerClick = useCallback(
    (speakerId: number) => {
      const params = new URLSearchParams(locationSearch);
      params.set('speaker', String(speakerId));
      history.push({ search: `?${params.toString()}` });
    },
    [history, locationSearch],
  );

  useEffect(() => {
    handlePageNavigation({ selected: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, scheduleDate]);

  const sessions = scheduleQuery.data?.schedule ?? [];
  return (
    <S.PageWrapper>
      <MetaTags title={t(I18N_SCHEDULE_TITLE)} />

      <S.MainContent>
        <S.PageHeader>
          <S.PageHeaderLeft>
            <S.BackButton type="button" onClick={goBack}>
              <FiChevronLeft size={18} />
            </S.BackButton>
            <h1>{t(I18N_SCHEDULE_TITLE)}</h1>
            {/* <S.CountBadge>
              {totalSessions} {t(I18N_SCHEDULE_TITLE).toLowerCase()}
            </S.CountBadge> */}
          </S.PageHeaderLeft>
        </S.PageHeader>

        <S.FiltersRow>
          <S.FiltersLeft>
            <DayTabList
              dates={formattedDates}
              activeDate={scheduleDate}
              onSelect={handleDateFilter}
            />
          </S.FiltersLeft>

          {eventHasManyRooms && selectRoomsQuery.data && (
            <S.FiltersRight>
              <RoomFilters
                rooms={selectRoomsQuery.data}
                activeRoomId={roomId}
                onFilter={handleRoomFilter}
              />
            </S.FiltersRight>
          )}
        </S.FiltersRow>

        <S.SessionGrid>
          {scheduleQuery.isLoading ? (
            <SkeletonCards />
          ) : (
            <SessionList sessions={sessions} onSessionClick={handleSession} />
          )}
        </S.SessionGrid>

        <Pagination
          pageCount={pagination}
          forcePage={currentPage - 1}
          onPageChange={handlePageNavigation}
        />
      </S.MainContent>

      <SpeakersSidebar
        isLoading={speakersQuery.isLoading}
        speakers={allSpeakers}
        liveSession={liveSession}
        nextSession={nextSession}
        onSpeakerClick={handleSpeakerClick}
        speakersLabel={t(I18N_SPEAKERS_TITLE)}
        onViewAll={() => history.push('/speakers')}
      />
    </S.PageWrapper>
  );
};

export default Schedule;
